import { CameraOff, ScanLine } from 'lucide-react';
import { useQrScanner } from '../lib/useQrScanner.ts';

type Props = {
  active: boolean;
  onResult: (value: string) => void;
};

/**
 * Live camera viewport with a scanning reticle. Designed to sit inside a modal.
 * Falls back to an explanatory message when BarcodeDetector / camera is absent.
 */
export function QRScanner({ active, onResult }: Props) {
  const { videoRef, state } = useQrScanner({ active, onResult });

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-border bg-bg-deep">
      <video ref={videoRef} className="h-full w-full object-cover" muted playsInline autoPlay />

      {(state === 'scanning' || state === 'starting') && (
        <>
          {/* Corner reticle */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative h-2/3 w-2/3">
              {[
                'left-0 top-0 border-l-2 border-t-2',
                'right-0 top-0 border-r-2 border-t-2',
                'bottom-0 left-0 border-b-2 border-l-2',
                'bottom-0 right-0 border-b-2 border-r-2',
              ].map((pos) => (
                <span key={pos} className={`absolute h-7 w-7 rounded-[3px] border-accent ${pos}`} />
              ))}
              {/* Sweeping scan line */}
              <span className="absolute inset-x-2 top-0 h-px animate-[scan_2.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-accent to-transparent shadow-[0_0_12px_var(--accent)] motion-reduce:hidden" />
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-8 text-xs text-white/90">
            <ScanLine className="h-3.5 w-3.5" />
            {state === 'starting' ? 'Kamera başlatılıyor…' : 'Karekodu çerçeveye getirin'}
          </div>
        </>
      )}

      {(state === 'unsupported' || state === 'denied' || state === 'error') && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
          <CameraOff className="h-7 w-7 text-text-faint" />
          <p className="text-sm text-text">
            {state === 'denied'
              ? 'Kamera izni verilmedi'
              : state === 'unsupported'
                ? 'Bu cihaz karekod taramayı desteklemiyor'
                : 'Kamera açılamadı'}
          </p>
          <p className="text-xs text-text-faint">Aşağıdan telefon veya referans ile arayın.</p>
        </div>
      )}
    </div>
  );
}
